/**
 * The React binding — one hook, so no face re-invents language state.
 *
 * `useLangState(storageKey, dict)` is everything a face needs: the stored choice,
 * the browser's preference when there is none, the document's `lang` attribute for
 * screen readers and the browser's own translation prompt, and a `t` bound to the
 * chosen language.
 *
 * A face with no provider gets English rather than an exception: a missing provider
 * is a wiring mistake, and blanking a staff screen to report it would be worse than
 * the mistake.
 */
import {createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode} from 'react';
import {documentLang, storedLang, translateIn, type Dictionary, type Lang, type Vars} from './index.js';

/** The React entry re-exports the core, so a face imports from one place:
 *  `import {useLangState, type Lang} from '@aiw/i18n/react'`. */
export * from './index.js';

export type LangState<S extends string = string> = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
  t: (id: S, vars?: Vars) => string;
};

export function useLangState<S extends string>(storageKey: string, dict: Record<Lang, Dictionary<S>>): LangState<S> {
  const [lang, setLangState] = useState<Lang>(() => storedLang(storageKey));
  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try { globalThis.localStorage?.setItem(storageKey, next); } catch { /* private mode */ }
    documentLang(next);
  }, [storageKey]);
  useEffect(() => { documentLang(lang); }, [lang]);
  const t = useCallback((id: S, vars?: Vars) => translateIn(dict, lang, id, vars), [dict, lang]);
  return useMemo(() => ({lang, setLang, toggle: () => setLang(lang === 'zh' ? 'en' : 'zh'), t}), [lang, setLang, t]);
}

/** A typed context + provider + hook for one face's dictionary. */
export function makeLanguageContext<S extends string>(dict: Record<Lang, Dictionary<S>>) {
  const Context = createContext<LangState<S> | null>(null);
  const fallback: LangState<S> = {
    lang: 'en',
    setLang: () => {},
    toggle: () => {},
    t: (id, vars) => translateIn(dict, 'en', id, vars),
  };
  const useT = (): LangState<S> => useContext(Context) ?? fallback;
  const LanguageProvider = ({state, children}: {state: LangState<S>; children: ReactNode}) =>
    <Context.Provider value={state}>{children}</Context.Provider>;
  return {LanguageContext: Context, LanguageProvider, useT};
}
